// Resolución de agent_id → nombre visible para la tabla de agentes.
//
// El endpoint de reportes devuelve solo ids (por_agente[].agent_id); los
// nombres se resuelven con el MISMO fetch que AgentPicker: getActiveUsers con
// límite alto trae el equipo completo de una vez (es chico) y evita paginar.
// Si el fetch falla, la tabla NO se rompe: cae al id truncado (8 chars + …),
// que al menos permite distinguir filas.

import { useCallback, useEffect, useState } from "react";
import { getActiveUsers } from "../../../../api/user";

/**
 * Nombre a mostrar de un usuario del BFF: "nombre apellido" o su email.
 * Mismo criterio que displayNameOf en AgentPicker.
 * @param {object} u
 * @returns {string}
 */
function displayNameOf(u) {
  return `${u?.name || ""} ${u?.lastName || ""}`.trim() || u?.email || "";
}

/**
 * Iniciales para el Avatar a partir del nombre ya resuelto (no del usuario:
 * en el fallback solo tenemos el id truncado y se usa su primer carácter).
 * @param {string} name
 * @returns {string}
 */
export function initialsOfName(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0].charAt(0);
  const second = parts.length > 1 ? parts[1].charAt(0) : "";
  return `${first}${second}`.toUpperCase();
}

/**
 * Hook: mapa _id → nombre, cargado UNA sola vez al montar.
 * @returns {{agentNameOf: (id: string) => string, loading: boolean}}
 */
export function useAgentNames() {
  // null = cargando; Map (posiblemente vacío si falló) cuando resolvió.
  const [names, setNames] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Misma llamada que AgentPicker / la vista /admin/users.
        const res = await getActiveUsers(1, 10000, "", "all", "", "", "");
        if (res?.error) throw new Error("fetch users failed");
        const docs = res?.data?.docs || res?.data?.users || [];
        const map = new Map();
        docs.forEach((u) => {
          const id = u?._id || u?.id;
          if (!id) return;
          map.set(String(id), displayNameOf(u));
        });
        if (alive) setNames(map);
      } catch {
        // Mapa vacío ⇒ todos los agentes caen al fallback de id truncado.
        if (alive) setNames(new Map());
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const agentNameOf = useCallback(
    (id) => {
      if (!id) return "—";
      const name = names?.get(String(id));
      return name || `${String(id).slice(0, 8)}…`;
    },
    [names],
  );

  return { agentNameOf, loading: names === null };
}

export default useAgentNames;
